import LandingPageLoyaltyAirportTransfersTile from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyAirportTransfersTile'
import LandingPageLoyaltyDiscountedInsurance from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyDiscountedInsurance'
import LandingPageLoyaltyStayUpgrade from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyStayUpgrade'
import LandingPageLoyaltyLoungePass from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyLoungePass'
import LandingPageLoyaltyPhoneSupport from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyPhoneSupport'
import LandingPageLoyaltyTourExpCredit from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyTourExpCredit'
import LandingPageLoyaltyTravelShootCredit from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyTravelShootCredit'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLeadRegion from 'loyalty/selectors/getIsLoyaltyLeadRegion'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

interface Props {
  benefit: App.LoyaltyBenefit
}

function getBenefitLabelDisplay(benefit: App.LoyaltyBenefit, isLeadLoyaltyRegion: boolean, intl: IntlShape) {
  const { unlimited, value, remaining } = benefit
  if (remaining === 0) {
    const redeemedLabel = intl.formatMessage({
      id: 'EPpAGLpGcJglxFBHu5e5',
      defaultMessage: 'Redeemed',
      description: 'Label shown when a loyalty benefit has been fully redeemed',
    })
    return <BodyText variant="small">{redeemedLabel}</BodyText>
  }

  if (
    (benefit.code === 'travelshoot_credit' || benefit.code === 'tour_exp_credit') &&
    !isLeadLoyaltyRegion
  ) {
    const voucherLabel = intl.formatMessage(
      {
        id: 'jyN843NM46ONTaWVn9eS',
        defaultMessage: '{remaining}x {remaining, plural, one {voucher} other {vouchers}}',
        description: 'Label showing remaining voucher count for travel shoot or tour experience credits',
      },
      { remaining: remaining ?? 0 },
    )
    return <BodyText variant="small">{voucherLabel}</BodyText>
  }

  if (unlimited) {
    const unlimitedLabel = intl.formatMessage({
      id: 'pMz7q5LpLXwkyoKeK9nc',
      defaultMessage: 'Unlimited',
      description: 'Label shown when a loyalty benefit has unlimited uses',
    })
    return <BodyText variant="small">{unlimitedLabel}</BodyText>
  }

  if (value) {
    if (value.unit === 'PERCENTAGE') {
      const discountLabel = remaining === 1
        ? intl.formatMessage(
            {
              id: 'ErpDPWFSvNi_IN0cRDfX',
              defaultMessage: '{amount}% discount',
              description: 'Label showing percentage discount for a single remaining loyalty benefit',
            },
            { amount: value.amount },
          )
        : intl.formatMessage(
            {
              id: 'yJsHW0fE2Bb4_5h5O3JS',
              defaultMessage: '{remaining}x {amount}% discount',
              description: 'Label showing remaining count and percentage discount for loyalty benefit',
            },
            { remaining, amount: value.amount },
          )
      return (
        <BodyText variant="small">
          {discountLabel}
        </BodyText>
      )
    } else if (value.unit === 'CURRENCY') {
      const currencyAmount = <FormatCurrency value={value.amount} format="dynamic" />
      if (remaining === 1) {
        return (
          <BodyText variant="small">
            <FormattedMessage
              id="ZMCNcWRsOwz_m989rh2c"
              defaultMessage="{amount} {remaining, plural, one {voucher} other {vouchers}}"
              description="Single remaining currency voucher loyalty benefit display"
              values={{
                remaining: remaining || 0,
                amount: currencyAmount,
              }}
            />
          </BodyText>
        )
      }
      return (
        <BodyText variant="small">
          <FormattedMessage
            id="CrdT-0bch1HgqsbYovuo"
            defaultMessage="{remaining}x {amount} {remaining, plural, one {voucher} other {vouchers}}"
            description="Multiple remaining currency voucher loyalty benefit display with count"
            values={{
              remaining: remaining || 0,
              amount: currencyAmount,
            }}
          />
        </BodyText>
      )
    }
  }

  const availableLabel = intl.formatMessage(
    {
      id: 'RkJythas5VkZ-xAYEUon',
      defaultMessage: '{remaining}x available',
      description: 'Label showing remaining available count for a loyalty benefit',
    },
    { remaining },
  )
  return (
    <BodyText variant="small">
      {availableLabel}
    </BodyText>
  )
}

function LoyaltyAccountLandingPageBenefitItem(props: Props) {
  const { benefit } = props
  const intl = useIntl()
  const isLeadLoyaltyRegion = useAppSelector(getIsLoyaltyLeadRegion)
  const benefitLabel = getBenefitLabelDisplay(benefit, isLeadLoyaltyRegion, intl)
  const [tier] = useCurrentLoyaltyTier()

  switch (benefit.code) {
    case 'airport_transfer':
      return <LandingPageLoyaltyAirportTransfersTile key={benefit.code} tier={tier} benefitLabel={benefitLabel} />
    case 'hotel_upgrade':
      return <LandingPageLoyaltyStayUpgrade key={benefit.code} tier={tier} benefitLabel={benefitLabel} />
    case 'lounge_pass':
      return <LandingPageLoyaltyLoungePass key={benefit.code} tier={tier} benefitLabel={benefitLabel} />
    case 'tour_exp_credit':
      return <LandingPageLoyaltyTourExpCredit key={benefit.code} tier={tier} benefit={benefit} benefitLabel={benefitLabel} />
    case 'travelshoot_credit':
      return <LandingPageLoyaltyTravelShootCredit key={benefit.code} tier={tier} benefit={benefit} benefitLabel={benefitLabel} />
    case 'discounted_insurance':
      if (!benefit.value) return null
      return <LandingPageLoyaltyDiscountedInsurance key={benefit.code} tier={tier} benefitLabel={benefitLabel} discountPercent={benefit.value.amount} />
    case 'phone_support':
      return <LandingPageLoyaltyPhoneSupport key={benefit.code} tier={tier} />
    default:
      return null
  }
}

export default LoyaltyAccountLandingPageBenefitItem
